import { SYNC_KEY_TERCEROS_PROMOTORES } from "../../../constantes";
import { PromotoresPlataformaService, PromotorPlataformaItem } from "../../../shared/generated/grpc/plataforma_terceros_promotores_pb";
import { PromotorCarteraItem } from "../../../shared/response/terceros/PromotorCarteraItem";
import { PromotorHistorialItem } from "../../../shared/response/terceros/PromotorHistorialItem";
import { PromotorResponse } from "../../../shared/response/terceros/PromotorResponse";
import { PromotorStatsResponse } from "../../../shared/response/terceros/PromotorStatsResponse";
import { ServiceGetListBase } from "../../../shared/services/base/ServiceGetListBase";
import { dateToMs, grpcCall, grpcClient } from "../../../shared/services/grpc/grpcCall";

/** Promotores (`PromotoresPlataformaService`, gRPC): listado en memoria y las consultas del detalle. */
export class PromotorService extends ServiceGetListBase<PromotorResponse> {
  static readonly instance = new PromotorService();
  private constructor() { super(); }

  /** Clave única del listado: deduplica `sync()` (dos pedidos simultáneos, un solo request). */
  protected readonly syncKey = SYNC_KEY_TERCEROS_PROMOTORES;

  /** gRPC-Web (`ListPromotores`): no eliminados, por nombre. */
  protected async fetchItems(): Promise<PromotorResponse[]> {
    const response = await grpcCall(() => grpcClient(PromotoresPlataformaService).listPromotores({}));
    return response.items.map(toPromotorResponse);
  }

  /** KPIs, gráfica, comparación con el equipo y distribución del período [desde, hasta] (ISO). */
  async getStats(promotorId: string, desde: string, hasta: string): Promise<PromotorStatsResponse> {
    const s = await grpcCall(() => grpcClient(PromotoresPlataformaService).getStats({
      promotorId, desde: dateToMs(desde) ?? 0n, hasta: dateToMs(hasta) ?? 0n,
    }));
    return {
      clientesActivos: s.clientesActivos,
      clientesNuevos: s.clientesNuevos,
      oportunidades: s.oportunidades,
      cierres: s.cierres,
      tasaConversion: s.tasaConversion,
      ventasTotales: Number(s.ventasTotales),
      ticketPromedio: Number(s.ticketPromedio),
      graficaMensual: s.graficaMensual.map((m) => ({
        mes: m.mes, oportunidades: m.oportunidades, cierres: m.cierres, ventas: Number(m.ventas),
      })),
      granularidadGrafica: s.granularidadGrafica as PromotorStatsResponse["granularidadGrafica"],
      vsEquipo: {
        tasaConversion: s.vsEquipo?.tasaConversion ?? 0,
        oportunidades: s.vsEquipo?.oportunidades ?? 0,
        ticketPromedio: Number(s.vsEquipo?.ticketPromedio ?? 0n),
        clientesNuevos: s.vsEquipo?.clientesNuevos ?? 0,
      },
      distribucionServicios: s.distribucionServicios.map((d) => ({ servicio: d.servicio, cantidad: d.cantidad })),
    };
  }

  /** Cartera formal del promotor (independiente del período). */
  async getCartera(promotorId: string): Promise<PromotorCarteraItem[]> {
    const r = await grpcCall(() => grpcClient(PromotoresPlataformaService).getCartera({ promotorId }));
    return r.items.map((c) => ({
      id: c.id,
      nombre: c.nombre,
      cantFincas: c.cantFincas,
      hectareasTotales: c.hectareasTotales,
      fechaVencimientoPlataforma: c.fechaVencimientoPlataforma !== undefined
        ? new Date(Number(c.fechaVencimientoPlataforma)).toISOString()
        : null,
      planActivo: c.planActivo,
      serviciosActivos: c.serviciosActivos,
      ultimaOportunidadFecha: c.ultimaOportunidadFecha !== undefined ? Number(c.ultimaOportunidadFecha) : null,
    }));
  }

  /** Oportunidades del promotor creadas en el período [desde, hasta] (ISO). */
  async getHistorial(promotorId: string, desde: string, hasta: string): Promise<PromotorHistorialItem[]> {
    const r = await grpcCall(() => grpcClient(PromotoresPlataformaService).getHistorial({
      promotorId, desde: dateToMs(desde) ?? 0n, hasta: dateToMs(hasta) ?? 0n,
    }));
    return r.items.map((h) => ({
      clienteId: h.clienteId,
      clienteNombre: h.clienteNombre,
      stage: h.stage,
      source: h.source,
      productos: h.productos
        ? {
          dataag: h.productos.dataag,
          rastro: h.productos.rastro,
          bascula: h.productos.bascula,
          vista: h.productos.vista,
          protec: { activo: h.productos.protecActivo },
        }
        : null,
      createdAt: Number(h.createdAt),
    }));
  }
}

/** Mensaje gRPC del promotor → `PromotorResponse`. Lo reusa el guardado. */
export function toPromotorResponse(p: PromotorPlataformaItem): PromotorResponse {
  return {
    id: p.id,
    isEliminado: false,
    nombre: p.nombre,
    email: p.email,
    telefono: p.telefono,
    tipo: p.tipo,
    isActivo: p.isActivo,
    usuarioPlataformaId: p.usuarioPlataformaId ?? null,
    enlazadoAUsuarioExistente: p.enlazadoAUsuarioExistente,
  };
}
